import { Box, Button, CircularProgress, Typography } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { Link } from "react-router-dom";
import {
  useCallback,
  useContext,
  useRef,
  useEffect,
  useLayoutEffect,
  type ReactNode,
  type RefObject,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { AgentThinking } from "./agent_thinking";
import { AwaitingResponse } from "./awaiting_response";
import { ChatButton } from "./chat_button";
import { SQLDisplay } from "./sql_display";
import { ChartDisplay } from "./chart_display";
import { GenericTableDisplay } from "./generic_table_display";
import { ToolCallsContainer } from "./tool_calls_container";
import { ChatContext } from "../../contexts/chat/context";
import { TableData } from "../../contexts/chat/type";
import { CHAT_FONT_STACK } from "../../contexts/material_ui/theme";
import { MessageGrounding } from "./message_grounding";
import { AtomicLogo } from "../../assets/AtomicLogo";

// Format timestamp as "H:MM AM/PM, Mon DD" in user's timezone
const formatMessageTimestamp = (timestamp: Date): string => {
  const date = new Date(timestamp);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  return `${hours}:${minutes} ${ampm}, ${month} ${day}`;
};

// Process message content to extract buttons and remove them from markdown content
const processMessageContent = (content: string) => {
  const buttonRegex = /\[CHAT_BUTTON:([^:]+):([^:]+):([^\]]+)\]/g;
  const buttons: Array<{ type: string; level: string; label: string }> = [];

  // Remove button syntax from markdown content and collect button data
  const processedContent = content.replace(
    buttonRegex,
    (match, type, level, label) => {
      buttons.push({ type, level, label });
      return ""; // Remove button from markdown content
    },
  );

  return { processedContent, buttons };
};

const processPlatformMessageContent = (content: string) => ({
  processedContent: content.replace(/\[CHAT_BUTTON:[^\]]{0,500}\]/g, ""),
  buttons: [] as Array<{ type: string; level: string; label: string }>,
});

const DataTable = ({ table }: { table: TableData }) => {
  return (
    <Box sx={{ my: 2 }}>
      {table.name && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {table.name}
        </Typography>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            minWidth: "max-content",
            fontSize: "13px",
          }}
        >
          <thead>
            <tr>
              {table.headers.map((header, i) => (
                <th
                  key={i}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    style={{
                      border: "1px solid #ddd",
                      padding: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      {table.summary && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
          {table.summary}
        </Typography>
      )}
    </Box>
  );
};

const RESPONSE_TABLE_LABEL =
  "Response table. Scroll horizontally to view all columns.";
const SURFACE_REFLOW_IDLE_MS = 160;

const expandedQuickActions = [
  {
    label: "Review this run",
    description: "Summarize supported planner takeaways.",
    prompt:
      "Summarize the most important planner takeaways supported by the selected run.",
  },
  {
    label: "Find planning risks",
    description: "Surface risks grounded in available run evidence.",
    prompt:
      "Which risks supported by the selected run should a planner review first?",
  },
  {
    label: "Compare recent runs",
    description: "Review supported changes from a comparable run.",
    prompt:
      "What changed between this run and the previous comparable run, based on available evidence?",
  },
  {
    label: "Check customer guidance",
    description: "Find documentation relevant to this view.",
    prompt: "Which customer documentation is relevant to the current view?",
  },
] as const;

const MarkdownTable = ({ children }: { children?: ReactNode }) => (
  <Box sx={{ my: 0.75, minWidth: 0, maxWidth: "100%" }}>
    <Typography
      aria-hidden="true"
      sx={{
        mb: 0.35,
        fontFamily: CHAT_FONT_STACK,
        fontSize: "0.65rem",
        lineHeight: 1.35,
        color: "text.secondary",
        textAlign: "right",
      }}
    >
      Scroll to see all columns →
    </Typography>
    <Box
      role="region"
      aria-label={RESPONSE_TABLE_LABEL}
      tabIndex={0}
      sx={{
        maxWidth: "100%",
        overflowX: "auto",
        pb: 0.5,
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(92, 96, 102, 0.42) transparent",
        "&::-webkit-scrollbar": { height: 6 },
        "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: 999,
          bgcolor: "rgba(92, 96, 102, 0.32)",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
          borderRadius: 0.75,
        },
      }}
    >
      <table>{children}</table>
    </Box>
  </Box>
);

export const MessageContainer = ({
  scrollContainerRef,
  surface = "sidebar",
  runLabel = "the selected run",
  welcomeComposer,
}: {
  scrollContainerRef?: RefObject<HTMLElement>;
  surface?: "sidebar" | "expanded";
  runLabel?: string;
  welcomeComposer?: ReactNode;
}) => {
  const {
    messages,
    isAwaitingResponse,
    sendMessage,
    agentState,
    transportKind,
    isConnected,
    isConnecting,
    chatContext,
    platformConversationHasEarlierMessages = false,
    platformEarlierMessagesLoading = false,
    platformEarlierMessagesError = null,
    loadEarlierPlatformConversationMessages,
  } = useContext(ChatContext);
  const isExpanded = surface === "expanded";
  const quickActionsAvailable =
    Boolean(chatContext) &&
    (transportKind !== "platform" || (isConnected && !isConnecting)) &&
    !isAwaitingResponse &&
    agentState.status !== "streaming" &&
    !platformEarlierMessagesLoading;
  const messageStackRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef(0);
  const shouldFollowStreamingRef = useRef(true);
  const streamingScrollFrameRef = useRef<number | null>(null);
  const previousSurfaceRef = useRef(surface);
  const surfaceReflowIntentRef = useRef<boolean | null>(null);
  const surfaceReflowReleaseTimerRef = useRef<number | null>(null);
  const earlierMessagesViewportRef = useRef<{
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);

  const clearSurfaceReflowIntent = useCallback(() => {
    surfaceReflowIntentRef.current = null;
    if (surfaceReflowReleaseTimerRef.current !== null) {
      window.clearTimeout(surfaceReflowReleaseTimerRef.current);
      surfaceReflowReleaseTimerRef.current = null;
    }
  }, []);

  const cancelSurfaceReflowForUser = useCallback(() => {
    if (surfaceReflowIntentRef.current === null) return;
    clearSurfaceReflowIntent();
    shouldFollowStreamingRef.current = false;
  }, [clearSurfaceReflowIntent]);

  const releaseOlderSurfaceReflowIntent = useCallback(() => {
    if (surfaceReflowIntentRef.current === false) {
      clearSurfaceReflowIntent();
    }
  }, [clearSurfaceReflowIntent]);

  const releaseSurfaceReflowAfterIdle = useCallback(() => {
    if (surfaceReflowReleaseTimerRef.current !== null) {
      window.clearTimeout(surfaceReflowReleaseTimerRef.current);
    }
    surfaceReflowReleaseTimerRef.current = window.setTimeout(() => {
      surfaceReflowIntentRef.current = null;
      surfaceReflowReleaseTimerRef.current = null;
    }, SURFACE_REFLOW_IDLE_MS);
  }, []);

  const loadEarlierMessages = () => {
    const scrollContainer = scrollContainerRef?.current;
    if (scrollContainer) {
      earlierMessagesViewportRef.current = {
        scrollHeight: scrollContainer.scrollHeight,
        scrollTop: scrollContainer.scrollTop,
      };
    }
    void loadEarlierPlatformConversationMessages?.().then((loaded) => {
      if (!loaded) {
        earlierMessagesViewportRef.current = null;
      }
    });
  };

  // Handle chat button clicks
  const handleButtonClick = (type: string) => {
    if (type === "CONFIRM") {
      sendMessage("confirm");
    }

    if (type === "DISCARD") {
      sendMessage("discard");
    }
  };

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    messagesEndRef.current?.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    const updateFollowPreference = () => {
      if (surfaceReflowIntentRef.current !== null) return;
      const distanceFromBottom =
        scrollContainer.scrollHeight -
        scrollContainer.scrollTop -
        scrollContainer.clientHeight;
      shouldFollowStreamingRef.current = distanceFromBottom <= 80;
    };

    updateFollowPreference();
    scrollContainer.addEventListener("scroll", updateFollowPreference, {
      passive: true,
    });
    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY < 0 && scrollContainer.scrollTop > 0) {
        cancelSurfaceReflowForUser();
      } else if (event.deltaY > 0) {
        releaseOlderSurfaceReflowIntent();
      }
    };
    let previousTouchY: number | null = null;
    const handleTouchStart = (event: TouchEvent) => {
      previousTouchY = event.touches[0]?.clientY ?? null;
    };
    const handleTouchMove = (event: TouchEvent) => {
      const currentTouchY = event.touches[0]?.clientY ?? null;
      if (
        currentTouchY !== null &&
        previousTouchY !== null &&
        currentTouchY > previousTouchY &&
        scrollContainer.scrollTop > 0
      ) {
        cancelSurfaceReflowForUser();
      } else if (
        currentTouchY !== null &&
        previousTouchY !== null &&
        currentTouchY < previousTouchY
      ) {
        releaseOlderSurfaceReflowIntent();
      }
      previousTouchY = currentTouchY;
    };
    const clearTouchPosition = () => {
      previousTouchY = null;
    };
    const handleScrollKey = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.closest(
            'input, textarea, select, [contenteditable="true"], [role="textbox"]',
          ))
      ) {
        return;
      }
      const movesTowardOlderHistory =
        ["ArrowUp", "PageUp", "Home"].includes(event.key) ||
        (event.key === " " && event.shiftKey);
      if (movesTowardOlderHistory && scrollContainer.scrollTop > 0) {
        cancelSurfaceReflowForUser();
      } else if (
        ["ArrowDown", "PageDown", "End"].includes(event.key) ||
        (event.key === " " && !event.shiftKey)
      ) {
        releaseOlderSurfaceReflowIntent();
      }
    };
    const handleScrollbarPointerDown = (event: PointerEvent) => {
      if (event.target !== scrollContainer || scrollContainer.scrollTop <= 0) {
        return;
      }
      const rect = scrollContainer.getBoundingClientRect();
      const scrollbarHitWidth = Math.max(
        rect.width - scrollContainer.clientWidth,
        16,
      );
      if (event.clientX >= rect.right - scrollbarHitWidth) {
        if (surfaceReflowIntentRef.current === false) {
          releaseOlderSurfaceReflowIntent();
        } else {
          cancelSurfaceReflowForUser();
        }
      }
    };
    scrollContainer.addEventListener("wheel", handleWheel, { passive: true });
    scrollContainer.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    scrollContainer.addEventListener("touchmove", handleTouchMove, {
      passive: true,
    });
    scrollContainer.addEventListener("touchend", clearTouchPosition, {
      passive: true,
    });
    scrollContainer.addEventListener("touchcancel", clearTouchPosition, {
      passive: true,
    });
    scrollContainer.addEventListener("keydown", handleScrollKey);
    scrollContainer.addEventListener("pointerdown", handleScrollbarPointerDown);
    return () => {
      scrollContainer.removeEventListener("scroll", updateFollowPreference);
      scrollContainer.removeEventListener("wheel", handleWheel);
      scrollContainer.removeEventListener("touchstart", handleTouchStart);
      scrollContainer.removeEventListener("touchmove", handleTouchMove);
      scrollContainer.removeEventListener("touchend", clearTouchPosition);
      scrollContainer.removeEventListener("touchcancel", clearTouchPosition);
      scrollContainer.removeEventListener("keydown", handleScrollKey);
      scrollContainer.removeEventListener(
        "pointerdown",
        handleScrollbarPointerDown,
      );
    };
  }, [
    cancelSurfaceReflowForUser,
    releaseOlderSurfaceReflowIntent,
    scrollContainerRef,
  ]);

  useLayoutEffect(() => {
    const surfaceChanged = previousSurfaceRef.current !== surface;
    previousSurfaceRef.current = surface;
    if (!surfaceChanged) return;

    surfaceReflowIntentRef.current = shouldFollowStreamingRef.current;
    releaseSurfaceReflowAfterIdle();
    if (surfaceReflowIntentRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [releaseSurfaceReflowAfterIdle, surface]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    const messageStack = messageStackRef.current;
    if (!scrollContainer || typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(() => {
      const surfaceReflowIntent = surfaceReflowIntentRef.current;
      if (surfaceReflowIntent !== null) {
        releaseSurfaceReflowAfterIdle();
      }
      if (
        surfaceReflowIntent === true ||
        (surfaceReflowIntent === null && shouldFollowStreamingRef.current)
      ) {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }
    });
    resizeObserver.observe(scrollContainer);
    if (messageStack) resizeObserver.observe(messageStack);
    return () => {
      resizeObserver.disconnect();
    };
  }, [releaseSurfaceReflowAfterIdle, scrollContainerRef]);

  // Preserve the existing append behavior; live token following is coalesced below.
  useEffect(() => {
    if (messages.length > previousMessageCountRef.current) {
      if (!earlierMessagesViewportRef.current) {
        scrollToBottom();
      }
    }
    earlierMessagesViewportRef.current = null;
    previousMessageCountRef.current = messages.length;
  }, [messages]);

  useLayoutEffect(() => {
    const viewport = earlierMessagesViewportRef.current;
    const scrollContainer = scrollContainerRef?.current;
    if (!viewport || !scrollContainer) return;
    scrollContainer.scrollTop =
      viewport.scrollTop +
      (scrollContainer.scrollHeight - viewport.scrollHeight);
  }, [messages, scrollContainerRef]);

  useEffect(() => {
    if (
      agentState.status === "streaming" &&
      agentState.streamedText &&
      shouldFollowStreamingRef.current &&
      streamingScrollFrameRef.current === null
    ) {
      streamingScrollFrameRef.current = window.requestAnimationFrame(() => {
        streamingScrollFrameRef.current = null;
        if (shouldFollowStreamingRef.current) {
          messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
        }
      });
    }
  }, [agentState.status, agentState.streamedText]);

  useEffect(
    () => () => {
      clearSurfaceReflowIntent();
      if (streamingScrollFrameRef.current !== null) {
        window.cancelAnimationFrame(streamingScrollFrameRef.current);
      }
    },
    [clearSurfaceReflowIntent],
  );

  return (
    <Box
      ref={messageStackRef}
      data-testid="nucleus-message-stack"
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: isExpanded ? 760 : "none",
        mx: isExpanded ? "auto" : 0,
        minWidth: 0,
        minHeight: isExpanded ? "100%" : 0,
        flexShrink: 0,
      }}
    >
      {transportKind === "platform" &&
        platformConversationHasEarlierMessages && (
          <Box sx={{ px: 1, pt: 0.75, pb: 0.25, textAlign: "center" }}>
            <Button
              size="small"
              variant="text"
              disabled={platformEarlierMessagesLoading}
              onClick={loadEarlierMessages}
              startIcon={
                platformEarlierMessagesLoading ? (
                  <CircularProgress size={14} />
                ) : undefined
              }
              sx={{ textTransform: "none" }}
            >
              {platformEarlierMessagesLoading
                ? "Loading earlier messages..."
                : "Load earlier messages"}
            </Button>
            {platformEarlierMessagesError && (
              <Typography
                role="alert"
                sx={{ color: "error.main", fontSize: "0.74rem" }}
              >
                {platformEarlierMessagesError}
              </Typography>
            )}
          </Box>
        )}
      {messages.length === 0 &&
        !isAwaitingResponse &&
        agentState.status !== "streaming" && (
          <Box
            sx={{
              mx: isExpanded ? 2 : 1,
              my: isExpanded ? 0 : 2,
              px: isExpanded ? { xs: 1, sm: 2 } : 2,
              py: isExpanded ? { xs: 3, md: 4 } : 2.25,
              flex: isExpanded ? 1 : "none",
              display: isExpanded ? "flex" : "block",
              flexDirection: isExpanded ? "column" : undefined,
              justifyContent: isExpanded ? "center" : undefined,
              textAlign: isExpanded ? "center" : "left",
              borderRadius: isExpanded ? 0 : 2.5,
              border: isExpanded ? "none" : "1px solid",
              borderColor: "rgba(56, 119, 247, 0.14)",
              background: isExpanded
                ? "transparent"
                : "linear-gradient(145deg, rgba(56, 119, 247, 0.06), rgba(255, 255, 255, 0.94) 64%)",
            }}
          >
            <Box
              data-testid={isExpanded ? "nucleus-welcome-logo" : undefined}
              sx={{
                width: isExpanded ? 48 : 34,
                height: isExpanded ? 48 : 34,
                mx: isExpanded ? "auto" : 0,
                mb: isExpanded ? 1.5 : 1.25,
                borderRadius: isExpanded ? 0 : "11px",
                display: "grid",
                placeItems: "center",
                color: "#2A5FDB",
                bgcolor: isExpanded ? "transparent" : "rgba(56, 119, 247, 0.1)",
                transformOrigin: "center",
                "@keyframes nucleusWelcomeFloat": {
                  "0%, 100%": {
                    transform: "translateY(0) rotate(0deg) scale(1)",
                    filter: "drop-shadow(0 0 0 rgba(56, 119, 247, 0))",
                  },
                  "50%": {
                    transform: "translateY(-4px) rotate(4deg) scale(1.02)",
                    filter: "drop-shadow(0 8px 12px rgba(56, 119, 247, 0.18))",
                  },
                },
                "@media (prefers-reduced-motion: no-preference)": {
                  animation: isExpanded
                    ? "nucleusWelcomeFloat 4.8s ease-in-out infinite"
                    : "none",
                },
              }}
            >
              {isExpanded ? (
                <AtomicLogo width={44} height={44} />
              ) : (
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 19 }} />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: isExpanded ? "1.55rem" : "0.92rem",
                lineHeight: 1.35,
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {isExpanded ? "Ask Nucleus." : "Ask about your planning data"}
            </Typography>
            <Typography
              sx={{
                mt: isExpanded ? 0.75 : 0.55,
                mx: isExpanded ? "auto" : 0,
                maxWidth: isExpanded ? 520 : "none",
                fontSize: isExpanded ? "0.86rem" : "0.78rem",
                lineHeight: 1.55,
                color: "text.secondary",
              }}
            >
              {isExpanded
                ? `Answers are grounded in ${runLabel} and show their available sources.`
                : "Ask about your supply chain, the selected run, or customer documentation. Nucleus will tell you when live data inspection is unavailable."}
            </Typography>
            {isExpanded && welcomeComposer && (
              <Box sx={{ width: "100%", maxWidth: 620, mx: "auto", mt: 2.75 }}>
                {welcomeComposer}
              </Box>
            )}
            {isExpanded && (
              <Box sx={{ mt: 3.25, textAlign: "left" }}>
                <Typography
                  sx={{
                    mb: 0.9,
                    fontSize: "0.68rem",
                    lineHeight: 1.4,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#9AA5B5",
                  }}
                >
                  Quick actions
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  {expandedQuickActions.map((action, index) => {
                    const descriptionId = `nucleus-quick-action-${index}-description`;
                    return (
                      <Button
                        key={action.label}
                        aria-label={action.label}
                        aria-describedby={descriptionId}
                        variant="outlined"
                        disabled={!quickActionsAvailable}
                        onClick={() => sendMessage(action.prompt)}
                        sx={{
                          minHeight: 58,
                          justifyContent: "flex-start",
                          px: 1.4,
                          py: 1,
                          borderRadius: 2,
                          borderColor: "rgba(56, 119, 247, 0.18)",
                          color: "#2C56B0",
                          bgcolor: "#fff",
                          textTransform: "none",
                          "&:hover": {
                            borderColor: "rgba(56, 119, 247, 0.42)",
                            bgcolor: "rgba(56, 119, 247, 0.035)",
                          },
                          "&:focus-visible": {
                            outline: "2px solid #3877F7",
                            outlineOffset: 2,
                          },
                        }}
                      >
                        <Box
                          sx={{ display: "grid", gap: 0.2, textAlign: "left" }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: CHAT_FONT_STACK,
                              fontSize: "0.78rem",
                              lineHeight: 1.35,
                              fontWeight: 700,
                              color: "#2C56B0",
                            }}
                          >
                            {action.label}
                          </Typography>
                          <Typography
                            id={descriptionId}
                            component="span"
                            sx={{
                              fontFamily: CHAT_FONT_STACK,
                              fontSize: "0.68rem",
                              lineHeight: 1.4,
                              fontWeight: 400,
                              color: "#66748A",
                            }}
                          >
                            {action.description}
                          </Typography>
                        </Box>
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}
      {messages.map((message, index) => {
        const isTableMessage = message.dataType === "table" && message.tables;

        const { processedContent, buttons } = isTableMessage
          ? { processedContent: message.content, buttons: [] }
          : transportKind === "platform"
            ? processPlatformMessageContent(message.content)
            : processMessageContent(message.content);

        return (
          <Box
            key={message.id ?? index}
            sx={{
              alignSelf: message.type === "sent" ? "flex-end" : "flex-start",
              minWidth: 0,
              width: message.type === "received" ? "100%" : "auto",
              maxWidth: message.type === "sent" ? "85%" : "100%",
              boxSizing: "border-box",
              flexShrink: 0,
            }}
          >
            <Box
              data-testid={
                message.type === "sent"
                  ? "nucleus-user-message"
                  : "nucleus-assistant-message"
              }
              sx={{
                my: 0.8,
                mx: message.type === "sent" ? 0.75 : 0,
                p: message.type === "sent" ? 1.35 : 0.75,
                borderRadius:
                  message.type === "received" ? 0 : "18px 18px 4px 18px",
                backgroundColor:
                  message.type === "sent" ? "#eaf2ff" : "transparent",
                border: "none",
                boxShadow: "none",
                "& .markdown-content": {
                  "& p": {
                    margin: 0,
                    marginBottom: "0.5em",
                    "&:last-child": {
                      marginBottom: 0,
                    },
                  },
                  "& h1, & h2, & h3, & h4, & h5, & h6": {
                    marginTop: "0.5em",
                    marginBottom: "0.5em",
                    fontWeight: 600,
                    "&:first-child": {
                      marginTop: 0,
                    },
                  },
                  "& h1": { fontSize: "1.5em" },
                  "& h2": { fontSize: "1.3em" },
                  "& h3": { fontSize: "1.1em" },
                  "& ul, & ol": {
                    paddingLeft: "1.5em",
                    margin: "0.5em 0",
                  },
                  "& li": {
                    marginBottom: "0.25em",
                  },
                  "& code": {
                    backgroundColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(0,0,0,0.06)",
                    padding: "0.1em 0.3em",
                    borderRadius: "3px",
                    fontSize: "0.9em",
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  },
                  "& pre": {
                    backgroundColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.15)"
                        : "rgba(0,0,0,0.04)",
                    padding: "0.75em",
                    borderRadius: "6px",
                    overflow: "auto",
                    margin: "0.5em 0",
                    "& code": {
                      backgroundColor: "transparent",
                      padding: 0,
                      fontSize: "0.85em",
                    },
                  },
                  "& blockquote": {
                    borderLeft: "3px solid",
                    borderColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.5)"
                        : "rgba(0,0,0,0.2)",
                    paddingLeft: "1em",
                    marginLeft: 0,
                    marginRight: 0,
                    fontStyle: "italic",
                  },
                  "& table": {
                    borderCollapse: "collapse",
                    boxSizing: "border-box",
                    minWidth: "100%",
                  },
                  "& th, & td": {
                    border: "1px solid",
                    borderColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.1)",
                    padding: "0.5em",
                    textAlign: "left",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                  },
                  "& th": {
                    backgroundColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.03)",
                    fontWeight: 600,
                  },
                  "& strong": {
                    fontWeight: 600,
                  },
                  "& em": {
                    fontStyle: "italic",
                  },
                  "& hr": {
                    border: "none",
                    borderTop: "1px solid",
                    borderColor:
                      message.type === "sent"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.1)",
                    margin: "1em 0",
                  },
                  "& a": {
                    color: message.type === "sent" ? "#87CEEB" : "#0066cc",
                    textDecoration: "underline",
                    "&:hover": {
                      textDecoration: "none",
                    },
                  },
                  "& img": {
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "6px",
                  },
                  "& .emoji": {
                    fontSize: "1.2em",
                  },
                },
              }}
            >
              <Box sx={{ overflowX: "auto" }}>
                {isTableMessage && message.content && (
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {message.content}
                  </Typography>
                )}

                {isTableMessage && message.tables ? (
                  <>
                    {message.tables.map((table, idx) => (
                      <DataTable key={idx} table={table} />
                    ))}
                  </>
                ) : (
                  <Box className="markdown-content">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Custom renderers for better typography
                        p: ({ children }) => (
                          <Typography
                            component="p"
                            variant="body2"
                            sx={{
                              color: "inherit",
                              lineHeight: 1.6,
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        li: ({ children }) => (
                          <Typography
                            component="li"
                            variant="body2"
                            sx={{
                              color: "inherit",
                              lineHeight: 1.6,
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        a: ({ children, href }) =>
                          typeof href === "string" &&
                          href.startsWith("/") &&
                          !href.startsWith("//") ? (
                            <Link to={href}>{children}</Link>
                          ) : (
                            <span>{children}</span>
                          ),
                        img: ({ alt }) => (
                          <span>
                            {typeof alt === "string" && alt.trim()
                              ? alt
                              : "Image omitted"}
                          </span>
                        ),
                        table: ({ children }) => (
                          <MarkdownTable>{children}</MarkdownTable>
                        ),
                      }}
                    >
                      {processedContent}
                    </ReactMarkdown>
                    {buttons.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {buttons.map((button, buttonIndex) => (
                          <ChatButton
                            key={buttonIndex}
                            label={button.label}
                            level={button.level}
                            type={button.type as "CONFIRM" | "DISCARD"}
                            onButtonClick={handleButtonClick}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Display SQL queries if present */}
                {message.type === "received" &&
                  transportKind !== "platform" &&
                  message.sqlQueries &&
                  message.sqlQueries.length > 0 && (
                    <SQLDisplay queries={message.sqlQueries} />
                  )}

                {/* Display charts if present */}
                {message.type === "received" &&
                  message.charts &&
                  message.charts.length > 0 && (
                    <ChartDisplay charts={message.charts} />
                  )}

                {/* Display structured non-SQL tables if present */}
                {message.type === "received" &&
                  message.genericTables &&
                  message.genericTables.length > 0 && (
                    <GenericTableDisplay tables={message.genericTables} />
                  )}

                {message.type === "received" &&
                  message.toolCalls &&
                  message.toolCalls.length > 0 && (
                    <ToolCallsContainer
                      toolCalls={message.toolCalls}
                      isStreaming={false}
                    />
                  )}

                {message.type === "received" && message.nucleusGrounding && (
                  <MessageGrounding
                    grounding={message.nucleusGrounding}
                    sources={message.nucleusSources}
                  />
                )}

                <Typography
                  variant="caption"
                  sx={{ mt: 1, opacity: 0.8, display: "block" }}
                >
                  {formatMessageTimestamp(message.timestamp)}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
      {/* Show AgentThinking when streaming, AwaitingResponse as fallback */}
      {agentState.status === "streaming" ? (
        <>
          <AgentThinking />
          <ToolCallsContainer
            toolCalls={agentState.toolCalls}
            isStreaming={true}
          />
        </>
      ) : (
        isAwaitingResponse && <AwaitingResponse />
      )}
      <div ref={messagesEndRef} />
    </Box>
  );
};
